/**
 * A tiny, dependency-free Markdown subset for rendering assistant replies:
 * paragraphs, unordered/ordered lists, fenced code blocks, and inline **bold**
 * and `code`. Parsing is pure (no DOM/React) so it is unit-testable; the Coach
 * page maps the returned blocks/spans to React elements.
 *
 * Deliberately minimal — no headings, links, images, or HTML passthrough — so
 * model output is rendered as inert text, never interpreted markup.
 */

export type InlineSpan =
  | { type: 'text'; text: string }
  | { type: 'bold'; text: string }
  | { type: 'code'; text: string };

export type Block =
  | { type: 'p'; spans: InlineSpan[] }
  | { type: 'ul'; items: InlineSpan[][] }
  | { type: 'ol'; items: InlineSpan[][] }
  | { type: 'code'; text: string };

const BULLET = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;

/** Parse inline **bold** and `code` within a single line of text. */
export function parseInline(text: string): InlineSpan[] {
  const spans: InlineSpan[] = [];
  // Alternate matches of `code` and **bold**; everything else is plain text.
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) spans.push({ type: 'text', text: text.slice(last, m.index) });
    if (m[1]) spans.push({ type: 'code', text: m[1].slice(1, -1) });
    else if (m[2]) spans.push({ type: 'bold', text: m[2].slice(2, -2) });
    last = m.index + m[0].length;
  }
  if (last < text.length) spans.push({ type: 'text', text: text.slice(last) });
  return spans.length > 0 ? spans : [{ type: 'text', text: '' }];
}

/** Parse markdown-lite source into a flat list of blocks. */
export function parseMarkdownLite(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'p', spans: parseInline(paragraph.join(' ').trim()) });
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // Fenced code block.
    if (line.trimStart().startsWith('```')) {
      flushParagraph();
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trimStart().startsWith('```')) {
        body.push(lines[i]!);
        i++;
      }
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet) {
      flushParagraph();
      const items: InlineSpan[][] = [parseInline(bullet[1]!)];
      while (i + 1 < lines.length && BULLET.test(lines[i + 1]!)) {
        items.push(parseInline(BULLET.exec(lines[++i]!)![1]!));
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    const ordered = ORDERED.exec(line);
    if (ordered) {
      flushParagraph();
      const items: InlineSpan[][] = [parseInline(ordered[1]!)];
      while (i + 1 < lines.length && ORDERED.test(lines[i + 1]!)) {
        items.push(parseInline(ORDERED.exec(lines[++i]!)![1]!));
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    paragraph.push(line);
  }
  flushParagraph();
  return blocks;
}
